import React, { useState, useEffect } from 'react';
import styles from './BulkActionBar.module.scss';
import { Button, Modal } from '../../../../common/_custom';
import {
    archive,
    dataRoomMoveIcon,
    dataRoomMoveIconDisabled,
    downloadIcon,
    downloadIconDataRoomHovered,
} from '../../../../assets/hostedassets';
import LoadingSpinner from '../../../../common/_custom/LoadingSpinner/LoadingSpinner';
import clsx from 'clsx';

interface BulkActionBarProps {
    selectedCount: number;
    onArchive?: () => void;
    onMove?: () => void;
    onDownload?: () => void;
    onMoveToActive?: () => void;
    onClearSelection: () => void;
    isMoveDisabled?: boolean;
    showArchiveButton?: boolean;
    showMoveButton?: boolean;
    isArchived?: boolean;
    loading?: boolean;
    isVisible?: boolean;
}

const BulkActionBar: React.FC<BulkActionBarProps> = ({
    selectedCount,
    onArchive,
    onMove,
    onDownload,
    onMoveToActive,
    onClearSelection,
    isMoveDisabled = false,
    showArchiveButton = false,
    showMoveButton = false,
    isArchived = false,
    loading = false,
    isVisible = false,
}) => {
    const [showArchiveConfirmation, setShowArchiveConfirmation] = useState(false);
    const [isAnimationVisible, setIsAnimationVisible] = useState(false);

    // Manage animation visibility
    useEffect(() => {
        if (isVisible) {
            // Small delay to trigger animation
            const timer = setTimeout(() => {
                setIsAnimationVisible(true);
            }, 10);
            return () => clearTimeout(timer);
        } else {
            setIsAnimationVisible(false);
        }
    }, [isVisible]);

    const handleArchiveClick = () => {
        setShowArchiveConfirmation(true);
    };

    const handleArchiveConfirm = () => {
        setShowArchiveConfirmation(false);
        onArchive?.();
    };

    return (
        <>
            <div className={clsx(styles.bulkActionBar, { [styles.visible]: isAnimationVisible })}>
                <div className={styles.leftSection}>
                    <span className={styles.selectionCount}>
                        {selectedCount} item{selectedCount !== 1 ? 's' : ''} selected
                    </span>
                    <button className={styles.clearButton} onClick={onClearSelection}>
                        Clear
                    </button>
                </div>
                <div className={styles.rightSection}>
                    {loading ? (
                        <LoadingSpinner color="#6021B3" />
                    ) : (
                        <>
                            {isArchived ? (
                                <>
                                    <Button
                                        text={
                                            <div className={styles.downloadButtonContent}>
                                                <img src={downloadIcon} alt="Download" />
                                                Download
                                            </div>
                                        }
                                        onClick={() => onDownload?.()}
                                        variant="purpleinverted"
                                        style={{
                                            margin: 0,
                                            border: '.0625rem solid #6021B3',
                                            padding: '10px 24px',
                                            backgroundColor: '#FFFFFF',
                                        }}
                                    />
                                    <Button
                                        text="Move to Active"
                                        onClick={() => onMoveToActive?.()}
                                        variant="purple"
                                        style={{
                                            margin: 0,
                                            padding: '10px 24px',
                                        }}
                                    />
                                </>
                            ) : (
                                <>
                                    <div className={styles.moveButtonWrapper}>
                                        {showMoveButton && (
                                            <Button
                                                text={
                                                    <div
                                                        className={`${styles.moveButtonContent} ${
                                                            isMoveDisabled ? styles.disableText : ''
                                                        }`}
                                                    >
                                                        <img
                                                            src={isMoveDisabled ? dataRoomMoveIconDisabled : dataRoomMoveIcon}
                                                            alt="Move"
                                                        />
                                                        Move
                                                    </div>
                                                }
                                                onClick={() => onMove?.()}
                                                variant="purpleinverted"
                                                disabled={isMoveDisabled}
                                                style={{
                                                    margin: 0,
                                                    border: isMoveDisabled ? '.0625rem solid #D0D5DD' : '.0625rem solid #6021B3',
                                                    padding: '10px 24px',
                                                    backgroundColor: '#FFFFFF',
                                                }}
                                            />
                                        )}
                                        {isMoveDisabled && <div className={styles.tooltip}>Folders can't move, select files to move</div>}
                                    </div>
                                    {showArchiveButton && (
                                        <Button
                                            text={
                                                <div className={styles.archiveButtonContent}>
                                                    <img src={archive} alt="Archive" />
                                                    Archive
                                                </div>
                                            }
                                            onClick={handleArchiveClick}
                                            variant="purpleinverted"
                                            style={{
                                                margin: 0,
                                                border: '.0625rem solid #6021B3',
                                                padding: '10px 24px',
                                                backgroundColor: '#FFFFFF',
                                            }}
                                        />
                                    )}
                                    <Button
                                        text={
                                            <div className={styles.downloadButtonContent}>
                                                <img src={downloadIcon} alt="Download" />
                                                Download
                                            </div>
                                        }
                                        onClick={() => onDownload?.()}
                                        variant="purple"
                                        style={{
                                            margin: 0,
                                            padding: '10px 24px',
                                        }}
                                    />
                                </>
                            )}
                        </>
                    )}
                </div>
            </div>

            {showArchiveConfirmation && (
                <Modal open={true} onClose={() => setShowArchiveConfirmation(false)} size="flexible" persistent={false}>
                    <div className={styles.confirmationModal}>
                        <h2 className={styles.confirmationHeading}>Archive Files</h2>
                        <p className={styles.confirmationMessage}>
                            Are you sure you want to archive {selectedCount} item{selectedCount !== 1 ? 's' : ''}?
                        </p>
                        <div className={styles.confirmationButtons}>
                            <Button
                                variant="purpleinverted"
                                text="Cancel"
                                onClick={() => setShowArchiveConfirmation(false)}
                                style={{ margin: 0, width: '100%' }}
                            />
                            <Button variant="purple" text="Archive" onClick={handleArchiveConfirm} style={{ margin: 0, width: '100%' }} />
                        </div>
                    </div>
                </Modal>
            )}
        </>
    );
};

export default BulkActionBar;
